/** @format */

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    TextField,
    Alert,
    InputAdornment,
    IconButton,
    Typography,
    CircularProgress,
    Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { AppDispatch, RootState } from "../../../redux/store/store";
import { loginBusinessUser } from "../../../redux/slices/businessAuthSlice";
import { CollectPrimaryButton } from "../styles";
import { colors } from "../../../utility/types/constant";
import { BusinessAuthShell } from "./BusinessAuthShell";

const BusinessSignIn: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isLoggingIn, loginError } = useSelector(
        (s: RootState) => s.businessAuth,
    );

    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const result = await dispatch(
            loginBusinessUser({ identifier: identifier.trim(), password }),
        );
        if (loginBusinessUser.fulfilled.match(result)) {
            navigate("/collect");
        }
    };

    return (
        <BusinessAuthShell
            title="Welcome back"
            subtitle="Sign in with your work email or business ID."
            footer={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    New here?{" "}
                    <Link
                        to="/collect/sign-up"
                        style={{ color: colors.primary, fontWeight: 700 }}
                    >
                        Create a business account
                    </Link>
                </Typography>
            }
        >
            <form onSubmit={handleSubmit} noValidate>
                {loginError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loginError}
                    </Alert>
                )}

                <TextField
                    label="Email or business ID"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword((v) => !v)}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ textAlign: "right", mt: 0.5 }}>
                    <Link
                        to="/collect/forgot-password"
                        style={{
                            color: colors.primary,
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            textDecoration: "none",
                        }}
                    >
                        Forgot password?
                    </Link>
                </Box>

                <CollectPrimaryButton
                    type="submit"
                    fullWidth
                    disabled={isLoggingIn}
                    sx={{ mt: 2 }}
                >
                    {isLoggingIn ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        "Sign in"
                    )}
                </CollectPrimaryButton>
            </form>
        </BusinessAuthShell>
    );
};

export default BusinessSignIn;
